import { LegalPageLayout } from '../components/legal/LegalPageLayout';
import { SiteHeader } from '../components/layout';
import { Footer } from '../components/landing';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-serif text-stone-900 dark:text-stone-100 mb-3">{title}</h2>
      {children}
    </section>
  );
}

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <SiteHeader mode="loading" />
      <LegalPageLayout title="Privacy Policy" label="Legal" lastUpdated="February 18, 2026">
      <p>
        Perch ("we," "us," or "our") operates the Perch flight price tracking service. This Privacy Policy
        explains how we collect, use, and protect your information when you use our service.
      </p>

      <Section title="Information We Collect">
        <p className="mb-3">We collect the following types of information:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Account information</strong> — When you sign in with Google,
            we receive your name and email address from Google OAuth.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Flight booking data</strong> — Confirmation codes, airline
            codes, flight segments, dates, routes, fare classes, and prices that you provide or that we extract
            from forwarded confirmation emails.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Price check data</strong> — Current prices and award
            availability we retrieve from third-party sources for your tracked bookings.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Usage analytics</strong> — We collect anonymous usage data
            through PostHog to understand how Perch is used and improve the service. You can opt out at
            any time in Settings &gt; Appearance.
          </li>
        </ul>
      </Section>

      <Section title="How We Use Your Information">
        <ul className="list-disc pl-5 space-y-2">
          <li>Track flight prices and award availability for your bookings</li>
          <li>Alert you when we find cheaper options via email or the dashboard</li>
          <li>Improve the service based on aggregated, anonymous usage patterns</li>
          <li>Communicate with you about your account and service updates</li>
        </ul>
      </Section>

      <Section title="Third-Party Services">
        <p className="mb-3">We use the following third-party services to operate Perch:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Supabase</strong> — Database hosting and
            authentication. Your data is stored in Supabase's infrastructure.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">PostHog</strong> — Anonymous usage analytics.
            PostHog collects anonymous interaction data to help us improve the service. You can opt out
            at any time in Settings &gt; Appearance.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Google Flights</strong> — We query Google Flights to
            check current cash prices for your tracked flights.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Seats.aero</strong> — We query Seats.aero to check
            award availability for your tracked flights.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Resend</strong> — We use Resend to send
            transactional email notifications (price drop alerts, booking confirmations).
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Cloudflare</strong> — Hosting and content delivery for
            the web application.
          </li>
        </ul>
      </Section>

      <Section title="Data Retention">
        <p>
          We retain your account and booking data for as long as your account is active. If you request
          account deletion, we schedule permanent removal after a 30-day grace period during which you
          can cancel the request. After the grace period, all associated data — including bookings, price
          history, and notifications — is permanently deleted.
        </p>
      </Section>

      <Section title="Your Rights">
        <p className="mb-3">You have the right to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Access your data</strong> — Download all your Perch data
            as a JSON file from Settings &gt; Account.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Delete your account</strong> — Request account deletion
            from Settings &gt; Account. Your data will be permanently removed after a 30-day grace period.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Export your data</strong> — Download a complete export of
            your profile, bookings, price history, and notification history at any time.
          </li>
          <li>
            <strong className="text-stone-900 dark:text-stone-100">Opt out of analytics</strong> — Disable PostHog
            analytics at any time from Settings &gt; Appearance.
          </li>
        </ul>
      </Section>

      <Section title="Cookies and Local Storage">
        <p>
          Perch uses local storage to remember your theme preference and analytics opt-out choice. PostHog
          may set cookies for anonymous session tracking. If you opt out of analytics in Settings, no
          tracking data is collected.
        </p>
      </Section>

      <Section title="Security">
        <p>
          We use industry-standard security practices including encrypted connections (HTTPS), row-level
          security policies on our database, and scoped authentication tokens. Your data is only accessible
          to your authenticated account.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For questions about this Privacy Policy or your data, contact us at{' '}
          <a
            href="mailto:saraswatayu@gmail.com"
            className="underline text-stone-900 dark:text-stone-100 hover:opacity-80"
          >
            saraswatayu@gmail.com
          </a>.
        </p>
      </Section>

      <Section title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any material changes
          by updating the "Last updated" date at the top of this page.
        </p>
      </Section>
      </LegalPageLayout>
      <Footer />
    </div>
  );
}
